import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  IconButton,
  Badge,
  Popover,
  List,
  ListItem,
  ListItemText,
  Typography,
  Button,
  Box,
  CircularProgress,
  Divider,
  Tooltip,
  Fade,
  Snackbar,
  Alert,
  Link,
  useTheme
} from '@mui/material';
import {
  NotificationsNone,
  NotificationsActive,
  CheckCircleOutline,
  ErrorOutline,
  DeleteSweep,
  ChevronRight,
  ArrowForward
} from '@mui/icons-material';
import { useAuthToken } from "../TokenContext";
import moment from 'moment';
import { useIntl } from "react-intl";

// Constants
const API_ENDPOINTS = {
  NOTIFICATIONS: process.env.REACT_APP_TALENT_CANDIDATENOTIFICATIONS,
  MARK_READ: process.env.REACT_APP_TALENT_NOTIFICATIONREAD
};

// Maximum number of notifications to show in dropdown
const MAX_NOTIFICATIONS_DISPLAY = 10;

// Notification Item Component
const NotificationItem = ({ notification, onMarkAsRead, isRemoved, onNotificationClick }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  // Get priority color based on notification type or priority if available
  const getPriorityColor = () => {
    if (notification.type === 'urgent' || notification.priority === 'high') {
      return isDarkMode ? '#ff5252' : '#f44336'; // Red for urgent
    } else if (notification.type === 'important' || notification.priority === 'medium') {
      return isDarkMode ? '#ffb74d' : '#ff9800'; // Orange for important
    }
    return isDarkMode ? '#42a5f5' : '#2196f3'; // Default blue
  };

  const borderColor = getPriorityColor();

  const handleMarkAsReadClick = (e) => {
    e.stopPropagation();
    onMarkAsRead(notification.id);
  };

  const handleItemClick = (e) => {
    e.preventDefault();
    onNotificationClick(notification);
  };

  return (
    <ListItem
      onClick={handleItemClick}
      sx={{
        transition: 'all 0.3s ease-in-out',
        transform: isRemoved ? 'translateX(100%)' : 'translateX(0)',
        opacity: isRemoved ? 0 : 1,
        maxHeight: isRemoved ? 0 : 'auto',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 1,
        borderLeft: `4px solid ${borderColor}`,
        backgroundColor: notification.read
          ? isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)'
          : isDarkMode ? 'rgba(144, 224, 239, 0.15)' : 'rgba(144, 224, 239, 0.1)',
        my: 1,
        borderRadius: 1,
        boxShadow: isDarkMode
          ? '0 1px 3px rgba(0,0,0,0.3)'
          : '0 1px 3px rgba(0,0,0,0.12)',
        '&:hover': {
          backgroundColor: notification.read
            ? isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.05)'
            : isDarkMode ? 'rgba(144, 224, 239, 0.25)' : 'rgba(144, 224, 239, 0.2)',
          cursor: 'pointer'
        },
        p: 2
      }}
    >
      <ListItemText
        primary={
          <Typography
            variant="body1"
            sx={{
              color: isDarkMode ? 'text.primary' : 'text.primary',
              fontWeight: notification.read ? 'normal' : 'bold',
              overflowWrap: 'break-word',
              wordWrap: 'break-word',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <span>{notification.message}</span>
            <ChevronRight
              fontSize="small"
              sx={{
                color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'action.active'
              }}
            />
          </Typography>
        }
        secondary={
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mt: 0.5,
              color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'text.secondary'
            }}
          >
            {notification.timeAgo}
          </Typography>
        }
      />
      {!notification.read && (
        <Tooltip title="Mark as read" placement="bottom" TransitionComponent={Fade}>
          <Button
            variant="contained"
            size="small"
            startIcon={<CheckCircleOutline />}
            sx={{
              bgcolor: isDarkMode ? '#0288d1' : '#90E0EF',
              color: isDarkMode ? '#ffffff' : '#0077b6',
              fontWeight: 'medium',
              '&:hover': {
                bgcolor: isDarkMode ? '#0277bd' : '#48cae4'
              },
              '&.clicked': {
                transform: 'scale(0.95)'
              },
              boxShadow: isDarkMode
                ? '0 2px 4px rgba(0,0,0,0.3)'
                : '0 2px 4px rgba(0,0,0,0.1)',
              transition: 'all 0.2s ease'
            }}
            onClick={handleMarkAsReadClick}
          >
            Mark as Read
          </Button>
        </Tooltip>
      )}
    </ListItem>
  );
};

const NotificationDropdown = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [allNotifications, setAllNotifications] = useState([]);
  const [removedIds, setRemovedIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const { user } = useSelector((state) => state.auth);
  const token = useAuthToken();
  const history = useHistory();
  const intl = useIntl();

  const fetchNotifications = useCallback(async () => {
    if (!token || !user?.data?.[0]?.c_id) {
      console.log('Token or user data is missing');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(API_ENDPOINTS.NOTIFICATIONS, {
        method: 'POST',
        headers: {
          Authorization: token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ candidate_id: user.data[0].c_id }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch notifications');
      }

      const { data } = await response.json();

      if (!data || !Array.isArray(data)) {
        setNotifications([]);
        setAllNotifications([]);
        return;
      }

      const processedNotifications = data
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        .map(notification => ({
          ...notification,
          timeAgo: moment(notification.createdAt).fromNow(),
          read: notification.notification_read === 1
        }));

      setAllNotifications(processedNotifications);
      setNotifications(processedNotifications.slice(0, MAX_NOTIFICATIONS_DISPLAY));
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setError('Could not load notifications. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [token, user]);

  // Initial fetch when component mounts
  useEffect(() => {
    fetchNotifications();

    // Refresh notifications periodically (every 5 minutes)
    /* istanbul ignore next */
    const intervalId = setInterval(() => {
      if (document.visibilityState === 'visible') {
        fetchNotifications();
      }
    }, 5 * 60 * 1000);

    return () => clearInterval(intervalId);
  }, [fetchNotifications]);

  // Additional fetch when dropdown opens
  useEffect(() => {
    if (anchorEl) {
      fetchNotifications();
    }
  }, [fetchNotifications, anchorEl]);

  const handleMarkAsRead = async (notificationId) => {
    try {
      const response = await fetch(API_ENDPOINTS.MARK_READ, {
        method: 'PUT',
        headers: {
          Authorization: token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ notification_ids: [notificationId] }),
      });

      if (!response.ok) {
        throw new Error('Failed to mark notification as read');
      }

      setRemovedIds(prev => [...prev, notificationId]);
      setAllNotifications(prev =>
        prev.map(note =>
          note.id === notificationId ? { ...note, read: true } : note
        )
      );
      setNotifications(prev =>
        prev.map(note =>
          note.id === notificationId ? { ...note, read: true } : note
        )
      );

      setSnackbar({
        open: true,
        message: 'Notification marked as read',
        severity: 'success'
      });

      // Refresh notifications after a short delay
      setTimeout(() => {
        fetchNotifications();
      }, 1000);

    } catch (error) {
      console.error('Error marking notification as read:', error);
      setSnackbar({
        open: true,
        message: 'Could not mark notification as read',
        severity: 'error'
      });
    }
  };

  const handleMarkAllAsRead = async () => {
    const unreadIds = notifications
      .filter(note => !note.read)
      .map(note => note.id);

    if (unreadIds.length === 0) {
      setSnackbar({
        open: true,
        message: 'No unread notifications',
        severity: 'info'
      });
      return;
    }

    try {
      const response = await fetch(API_ENDPOINTS.MARK_READ, {
        method: 'PUT',
        headers: {
          Authorization: token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ notification_ids: unreadIds }),
      });

      if (!response.ok) {
        throw new Error('Failed to mark all notifications as read');
      }

      setRemovedIds(prev => [...prev, ...unreadIds]);
      setAllNotifications(prev =>
        prev.map(note => ({ ...note, read: true }))
      );
      setNotifications(prev =>
        prev.map(note => ({ ...note, read: true }))
      );

      setSnackbar({
        open: true,
        message: `${unreadIds.length} notification${unreadIds.length > 1 ? 's' : ''} marked as read`,
        severity: 'success'
      });

      // Refresh notifications after a short delay
      setTimeout(() => {
        fetchNotifications();
      }, 1000);

    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      setSnackbar({
        open: true,
        message: 'Could not mark all notifications as read',
        severity: 'error'
      });
    }
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    fetchNotifications(); // Refresh on open
  };

  const handleClose = () => {
    setAnchorEl(null);
    setRemovedIds([]); // Reset removed IDs when closing
  };

  const handleSnackbarClose = (_, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleNotificationClick = (notification) => {
    // Mark notification as read if it's not already read
    if (!notification.read) {
      handleMarkAsRead(notification.id);
    }

    // Close the dropdown
    handleClose();

    // Navigate based on notification content/type
    // This is where we handle the routing logic based on the notification content
    if (notification.message.toLowerCase().includes('task') && notification.podID) {
      // Navigate to task management with the necessary pod info
      // Use viewTab: true to automatically switch to the View Tasks tab
      history.push({
        pathname: '/Createtasktalent',  // This route is handled by CreateTaskTalent component
        state: {
          podid: notification.podID,
          podname: notification.podName || '',
          viewTab: true // This signals to show the View Tasks tab
        }
      });
    } else if (notification.message.toLowerCase().includes('pod activity')) {
      // Navigate to pod activity page
      history.push({
        pathname: '/Podactivitytalent',
        state: {
          podid: notification.podID,
          viewTab: true
        }
      });
    } else {
      // Default navigation (e.g., to dashboard)
      history.push('/candidate-dashboard');
    }
  };

  const handleViewAllClick = () => {
    // Close the dropdown
    handleClose();

    // Navigate to notifications history page
    history.push('/notificationshistory');
  };

  const open = Boolean(anchorEl);
  const unreadCount = allNotifications.filter(note => !note.read).length;

  return (
    <Box>
      <Tooltip title="Notifications" placement="bottom">
        <IconButton
          onClick={handleClick}
          sx={{
            color: unreadCount > 0
              ? isDarkMode ? '#42a5f5' : '#0077b6'
              : isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'text.secondary',
            ml: 1,
            transition: 'transform 0.2s ease',
            '&:hover': {
              transform: 'scale(1.05)',
              bgcolor: isDarkMode
                ? 'rgba(66, 165, 245, 0.08)'
                : 'rgba(0, 119, 182, 0.08)'
            }
          }}
        >
          <Badge
            badgeContent={unreadCount}
            color="error"
            overlap="circular"
            sx={{
              '& .MuiBadge-badge': {
                fontSize: '0.65rem',
                height: '1.3rem',
                minWidth: '1.3rem',
                padding: '0 4px',
                top: 4,
                right: 4,
                bgcolor: isDarkMode ? '#f44336' : '#f44336',
                color: '#ffffff'
              }
            }}
          >
            {unreadCount > 0 ? <NotificationsActive /> : <NotificationsNone />}
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: { xs: '95vw', sm: 350 },
            maxWidth: { xs: '95vw', sm: 350 },
            maxHeight: 500,
            borderRadius: 2,
            boxShadow: isDarkMode
              ? '0 4px 20px rgba(0,0,0,0.5)'
              : '0 4px 20px rgba(0,0,0,0.15)',
            overflow: 'hidden',
            bgcolor: isDarkMode ? 'background.paper' : 'background.paper',
            border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.12)' : 'none'
          }
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f8f9fa',
            borderBottom: isDarkMode ? '1px solid rgba(255, 255, 255, 0.12)' : 'none'
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 'bold',
              fontSize: '1rem',
              color: isDarkMode ? 'text.primary' : 'text.primary'
            }}
          >
            {intl.formatMessage({ id: "notifications" })}
          </Typography>

          {unreadCount > 0 && (
            <Tooltip title="Mark all as read">
              <Button
                size="small"
                startIcon={<DeleteSweep />}
                onClick={handleMarkAllAsRead}
                sx={{
                  fontSize: '0.75rem',
                  color: isDarkMode ? '#42a5f5' : '#0077b6',
                  '&:hover': {
                    bgcolor: isDarkMode
                      ? 'rgba(66, 165, 245, 0.08)'
                      : 'rgba(0, 119, 182, 0.08)'
                  }
                }}
              >
                {intl.formatMessage({ id: "markAllAsRead" })}
              </Button>
            </Tooltip>
          )}
        </Box>

        <Divider sx={{
          bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'
        }} />

        {loading ? (
          <Box data-testid="loading-state" sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress
              size={28}
              sx={{
                color: isDarkMode ? '#42a5f5' : '#0077b6'
              }}
            />
          </Box>
        ) : error ? (
          <Box data-testid="error-state" sx={{ p: 3, textAlign: 'center' }}>
            <ErrorOutline
              color="error"
              sx={{
                mb: 1,
                fontSize: '2rem',
                color: isDarkMode ? '#ff5252' : 'error.main'
              }}
            />
            <Typography
              sx={{
                color: isDarkMode ? '#ff5252' : 'error.main'
              }}
            >
              {error}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={fetchNotifications}
              sx={{
                mt: 2,
                borderColor: isDarkMode ? '#42a5f5' : '#0077b6',
                color: isDarkMode ? '#42a5f5' : '#0077b6',
                '&:hover': {
                  borderColor: isDarkMode ? '#1976d2' : '#005582',
                  bgcolor: isDarkMode
                    ? 'rgba(66, 165, 245, 0.08)'
                    : 'rgba(0, 119, 182, 0.08)'
                }
              }}
            >
              {intl.formatMessage({ id: "tryAgain" })}
            </Button>
          </Box>
        ) : (
          <>
            <List sx={{ p: 0, maxHeight: 350, overflow: 'auto' }}>
              {notifications.length > 0 ? (
                notifications.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onMarkAsRead={handleMarkAsRead}
                    onNotificationClick={handleNotificationClick}
                    isRemoved={removedIds.includes(notification.id)}
                  />
                ))
              ) : (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <CheckCircleOutline
                    sx={{
                      fontSize: '3rem',
                      color: isDarkMode ? '#42a5f5' : '#90E0EF',
                      mb: 2
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'text.secondary'
                    }}
                  >
                    {intl.formatMessage({ id: "noNotificationsToDisplay" })}
                  </Typography>
                </Box>
              )}
            </List>

            {/* View More Link */}
            {allNotifications.length > MAX_NOTIFICATIONS_DISPLAY && (
              <Box
                sx={{
                  p: 2,
                  borderTop: isDarkMode
                    ? '1px solid rgba(255, 255, 255, 0.12)'
                    : '1px solid rgba(0, 0, 0, 0.12)',
                  textAlign: 'center',
                  backgroundColor: isDarkMode
                    ? 'rgba(255, 255, 255, 0.02)'
                    : 'rgba(0, 0, 0, 0.02)'
                }}
              >
                <Button
                  endIcon={<ArrowForward />}
                  onClick={handleViewAllClick}
                  sx={{
                    color: isDarkMode ? '#42a5f5' : '#0077b6',
                    fontWeight: 'medium',
                    textTransform: 'none',
                    '&:hover': {
                      bgcolor: isDarkMode
                        ? 'rgba(66, 165, 245, 0.08)'
                        : 'rgba(0, 119, 182, 0.08)'
                    }
                  }}
                >
                  {intl.formatMessage({ id: "viewAllNotifications" })} ({allNotifications.length})
                </Button>
              </Box>
            )}
          </>
        )}
      </Popover>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            width: '100%',
            bgcolor: isDarkMode && snackbar.severity === 'success'
              ? '#2e7d32'
              : undefined,
            color: isDarkMode ? '#ffffff' : undefined
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NotificationDropdown;